import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styles from './SubNavDropdown.module.less';
import { tabInfoPropType } from '../util';
import { trackGA } from 'services/track';

const SubNavDropdown = ({ tabInfo, closeDropdown, buttonRefs }) => {
  const { name, urlName, gradesForContentType, sections, seeAll } = tabInfo;
  const [fullOpacity, setFullOpacity] = useState(false);
  const dropdownRef = useRef(null);

  // DOM element of the SubNavButton associated with this dropdown. Necessary for accessibility tabbing.
  const buttonElem = buttonRefs[urlName] ? buttonRefs[urlName].current : null;

  const handleDropdownClick = (sectionName, text, url) => {
    closeDropdown();
    const gtmObject = {
      event: 'click_button',
      feature: 'Navigation',
      section: `${name} Sub-Menu`,
      button: `${sectionName} Links`,
      click_text: text,
      click_url: `https://${window.location.hostname}${url}`,
    };
    trackGA('Sub Menu', 'click_button', '', '', gtmObject);
  };

  useEffect(() => {
    // Trigger opacity animation. Seen when you go directly from one open dropdown button to another
    setFullOpacity(true);
    // TABBING (ACCESSIBILITY) SETUP:
    const dropdownToggle = buttonElem.querySelector('button');

    // Used to clear the event listener of the dropdownToggle button when we unmount the component
    let clearDropdownToggleListener = () => {};

    if (dropdownRef.current && dropdownRef.current.querySelector('a')) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();

      // Position the dropdown directly below the button
      const buttonRect = buttonElem.getBoundingClientRect();
      dropdownRef.current.style.left = `${buttonRect.left}px`;

      const secondaryItemsContainers = dropdownRef.current.querySelectorAll(
        '.secondaryItems'
      );

      const updateSectionHeight = e => {
        const mainItem = e.currentTarget;
        const secondaryContainer = mainItem.querySelector('.secondaryItems');
        if (secondaryContainer) {
          const section = mainItem.closest('.section');
          section.style.height = `${
            secondaryContainer.getBoundingClientRect().height
          }px`;
        }
      };

      // Constants for layout calculations
      const CONTAINER_TOP_OFFSET = -15; // px (align secondary container with main items)
      const CONTAINER_HEIGHT_BASE = 35; // px (base container height padding)

      const COLUMN_CONTAINER_SPACING = 30; // px (initial left position for first column)
      const COLUMN_SPACING = 60; // px (space between columns)

      const ITEMS_VERTICAL_OFFSET = 20; // px (top padding for first item in column)
      const ITEM_HEIGHT = 30; // px (height per menu item)

      // Dynamically calculate and set the height and width of the dropdown
      secondaryItemsContainers.forEach(container => {
        container.style.height = `${dropdownRect.height}px`;
        container.style.top = `${CONTAINER_TOP_OFFSET}px`;
        container.style.display = 'block';

        requestAnimationFrame(() => {
          const columns = ['.column-1', '.column-2', '.column-3', '.column-4'];
          let maxCount = 0;
          const columnWidths = [];

          // Calculate column widths and maximum item count
          columns.forEach(columnClass => {
            const columnItems = Array.from(
              container.querySelectorAll(columnClass)
            );
            const maxWidth = columnItems.reduce((width, item) => {
              return Math.max(width, item.getBoundingClientRect().width);
            }, 0);

            columnItems.forEach(item => {
              item.style.width = `${maxWidth}px`;
            });

            columnWidths.push(maxWidth);
            maxCount = Math.max(maxCount, columnItems.length);
          });

          // Adjust container height
          const newHeight = maxCount * ITEM_HEIGHT + CONTAINER_HEIGHT_BASE;
          if (newHeight > container.getBoundingClientRect().height) {
            container.style.height = `${newHeight}px`;
          }

          // Position columns
          let leftOffset = COLUMN_CONTAINER_SPACING;
          columns.forEach((columnClass, index) => {
            const columnItems = Array.from(
              container.querySelectorAll(columnClass)
            );
            if (columnItems.length > 0) {
              columnItems.forEach((item, itemIndex) => {
                item.style.position = 'absolute';
                item.style.left = `${leftOffset}px`;
                item.style.top = `${ITEMS_VERTICAL_OFFSET +
                  itemIndex * ITEM_HEIGHT}px`;
              });
              leftOffset += columnWidths[index] + COLUMN_SPACING;
            }
          });

          // Set container width (corrected logic)
          const nonZeroColumnWidths = columnWidths.filter(width => width > 0);
          const columnCount = nonZeroColumnWidths.length;

          if (columnCount > 0) {
            const totalColumnsWidth = nonZeroColumnWidths.reduce(
              (sum, width) => sum + width,
              0
            );
            const totalSpacing = (columnCount - 1) * COLUMN_SPACING;
            container.style.width = `${COLUMN_CONTAINER_SPACING * 2 +
              totalColumnsWidth +
              totalSpacing}px`;
          } else {
            container.style.width = '0';
          }

          // Hide the container after calculations
          container.style.display = '';
        });
      });

      // Add event listeners to main items to update section height on hover
      const mainItems = dropdownRef.current.querySelectorAll('.mainItem');
      mainItems.forEach(mainItem => {
        mainItem.addEventListener('mouseenter', updateSectionHeight);
      });

      // Remove the right border radius of the main item div if there are secondary items visible
      const updateBorderRadius = () => {
        if (dropdownRef.current) {
          const secondaryItemsVisible = Array.from(
            dropdownRef.current.querySelectorAll('.secondaryItems')
          ).some(item => {
            const computedStyle = window.getComputedStyle(item);
            return computedStyle.display === 'flex';
          });

          if (secondaryItemsVisible) {
            dropdownRef.current.classList.add('no-bottom-right-radius');
          } else {
            dropdownRef.current.classList.remove('no-bottom-right-radius');
          }
        }
      };

      // Call updateBorderRadius when the component mounts
      updateBorderRadius();

      // Add event listeners to update border radius on hover
      mainItems.forEach(mainItem => {
        mainItem.addEventListener('mouseenter', updateBorderRadius);
        mainItem.addEventListener('mouseleave', updateBorderRadius);
      });

      if (dropdownRef.current && buttonElem) {
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const buttonRect = buttonElem.getBoundingClientRect();

        // Calculate the left position to align the center of the dropdown with the center of the button
        const leftPosition =
          buttonRect.left + buttonRect.width / 2 - dropdownRect.width / 2;
        dropdownRef.current.style.left = `${leftPosition}px`;
      }

      return () => {
        mainItems.forEach(mainItem => {
          ['mouseenter', 'mouseleave'].forEach(event => {
            mainItem.removeEventListener(event, updateBorderRadius);
          });
          mainItem.removeEventListener('mouseenter', updateSectionHeight);
        });
      };
    }

    // While the dropdown is open, close it if the Escape key is pressed
    const closeDropdownOnEsc = event => {
      if (event.key !== 'Escape') return;
      closeDropdown(false);
      dropdownToggle.focus();
    };
    window.addEventListener('keyup', closeDropdownOnEsc);
    return () => {
      window.removeEventListener('keyup', closeDropdownOnEsc);
      clearDropdownToggleListener();
    };
  }, []);

  const buildItem = (item, className, sectionName) => {
    const hasSecondaryItems =
      item.secondary_items && item.secondary_items.length > 0;

    return (
      <li
        key={item.title.toLowerCase().replace(/\s/g, '')}
        className={clsx(
          className.split(' ').map(name => styles[name]),
          className,
          hasSecondaryItems && 'hasSecondaryItems'
        )}
      >
        {item.type === 'Empty Space' ? (
          <br />
        ) : (
          <a
            href={item.url}
            onClick={() =>
              handleDropdownClick(sectionName, item.title, item.url)
            }
          >
            {item.title}
          </a>
        )}
        {hasSecondaryItems && (
          <div className={`${styles.secondaryItems} secondaryItems`}>
            {item.secondary_items.map(secondaryItem => {
              return (
                <a
                  key={secondaryItem.title.toLowerCase().replace(/\s/g, '')}
                  href={secondaryItem.url}
                  className={clsx(
                    styles.childItem,
                    styles.items,
                    'childItem',
                    `column-${secondaryItem.column}`
                  )}
                >
                  {secondaryItem.title}
                </a>
              );
            })}
          </div>
        )}
      </li>
    );
  };

  const buildColumn = (column, addSeeAll, columnIndex, sectionName) => {
    const itemList = [];
    for (let i = 0; i < column.length; i += 1) {
      let className = 'childItem';
      if (column[i].type === 'Main Item') {
        className = 'mainItem';
        if (i === 0) {
          className = 'mainItem mainItemNoTopMargin';
        } else if (column[i - 1].type === 'Main Item') {
          className = 'mainItem mainItemTopMargin';
        }
      }
      if (column[i].button) className += ' hiddenButton';

      itemList.push(buildItem(column[i], className, sectionName));
    }
    return (
      <div
        key={`column${columnIndex}`}
        className={clsx(styles.column, `column-${columnIndex}`)}
      >
        {itemList}
        {addSeeAll && seeAll && (
          <li key="seeAllLink" className={styles.seeAllLink}>
            <a href={seeAll.url}>{seeAll.text}</a>
          </li>
        )}
      </div>
    );
  };

  const buildSection = section => {
    let columns = section.items.reduce((c, item) => {
      c[item.column] = c[item.column] || [];
      c[item.column].push(item);
      return c;
    }, {});
    const addSeeAll = section.title.toUpperCase() === 'ADDITIONAL TOPICS';
    columns = Object.values(columns);
    const columnList = columns.map((value, i) =>
      buildColumn(
        value,
        addSeeAll && i + 1 === columns.length,
        i,
        section.title
      )
    );
    return (
      <div key={section.title} className={clsx(styles.section, 'section')}>
        <h6>{section.title}</h6>
        <div className={styles.subNavColumns}>{columnList}</div>
      </div>
    );
  };

  const buildSectionsWithGrade = () => {
    const gradeList = gradesForContentType
      ? gradesForContentType.map((grade, i) => {
          const url = `/${urlName}/${grade.urlName}/`;
          const className = i
            ? styles.mainItemTopMargin
            : styles.mainItemNoTopMargin;
          return (
            <li
              key={url}
              className={clsx(styles.mainItem, className, 'mainItem')}
            >
              <a
                href={url}
                onClick={() =>
                  handleDropdownClick('By Grade', grade.longDisplay, url)
                }
              >
                {grade.longDisplay}
              </a>
            </li>
          );
        })
      : null;

    let tabSections = [];

    if (gradeList) {
      tabSections.push(
        <div key="grade" className={clsx(styles.section, 'section')}>
          <h6>By grade</h6>
          <div className={styles.subNavColumns}>
            <div className={styles.column}>{gradeList}</div>
          </div>
        </div>
      );
    }

    const otherSections = sections.map(buildSection);

    tabSections = tabSections.concat(otherSections);

    tabSections = tabSections.flatMap((value, index) =>
      tabSections.length - 1 === index
        ? value
        : [
            value,
            <div key={`divider${index}`} className={styles.spacer}>
              <div className={styles.headerDivider} />
            </div>,
          ]
    );

    return tabSections;
  };

  const sectionsWithGrade = buildSectionsWithGrade();

  return (
    <div
      className={styles.subNavDropdown}
      ref={dropdownRef}
      data-cy="sub-nav-dropdown"
    >
      <div className={clsx(styles.container, fullOpacity && styles.open)}>
        {sectionsWithGrade}
      </div>
    </div>
  );
};

SubNavDropdown.propTypes = {
  tabInfo: tabInfoPropType.isRequired,
  closeDropdown: PropTypes.func.isRequired,
  buttonRefs: PropTypes.shape({
    urlName: PropTypes.shape({ current: PropTypes.object }),
  }),
};

SubNavDropdown.defaultProps = {
  buttonRefs: null,
};

export default SubNavDropdown;
